import { useState } from "react";
import { useEpgData } from "../../hooks/useEpgData";
import dayjs from "dayjs";

import { ChartControls } from "../../components/Channel";
import { useChannelGridCompare } from "../../hooks/useChannelGridCompare";
import MultiLineAvgChart from "../../components/charts3/MultiLineAvgChart";
import MultiLineAvgChartTV8 from "../../components/charts3/MultiLineAvgChartTV8";

import "./Channel.css";
import { programEndClock } from "../../utils/epgTime";

export default function ChannelGrid({
  rawVideo,
  ttvRawVideo,
  channelId,
  refreshEnabled,
  refreshKey,
  setRefreshEnabled,
  refreshIntervalMin,
  setRefreshIntervalMin,
  refreshCountdown,
  manualRefresh,
  enableFourthLine,
  isTV8 = false
}) {
  const MODE = "minute";

  const storedUser = JSON.parse(localStorage.getItem("adm_panel_user") || "{}");
  const TTVAllowance = !!storedUser.ttvallowance;

  /* =========================
     DATE (SINGLE DAY)
  ========================= */

  const today = dayjs().format("YYYY-MM-DD");
  const [date, setDate] = useState(today);
  // ✅ FOURTH DATE
  const [fourthDate, setFourthDate] = useState(null);

  /* =========================
     TIME RANGE
  ========================= */

  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");

  /* =========================
     DATA
  ========================= */

  const series = useChannelGridCompare({
    rawVideo,
    selectedDate: date,
    fourthDate: enableFourthLine ? fourthDate : null, // ✅ kritik
    startTime,
    endTime,
    ttvRawVideo: TTVAllowance ? ttvRawVideo : null,
  });

  const hasData = series.some(
    (s) => Array.isArray(s.data) && s.data.length > 0
  );

  /* =========================
     EPG
  ========================= */

  const { epgBlocks, loading: epgLoading } = useEpgData(channelId, date);
  const [selectedProgram, setSelectedProgram] = useState(null);

  const epgForSelectedDate = epgBlocks.filter(b => b.keyDate === date);

  return (
    <div className="channel-section">
      {/* CONTROLS */}
      <ChartControls
        channelId={channelId}
        mode={MODE}
        hideModeButtons
        startDateInput={date}
        endDateInput={date}
        onStartDateChange={setDate}
        onEndDateChange={setDate}
        startTime={startTime}
        endTime={endTime}
        onStartTimeChange={setStartTime}
        onEndTimeChange={setEndTime}
        refreshEnabled={refreshEnabled}
        onRefreshToggle={setRefreshEnabled}
        refreshIntervalMin={refreshIntervalMin}
        onRefreshIntervalChange={setRefreshIntervalMin}
        countdown={refreshCountdown}
        onManualRefresh={manualRefresh}
        enableFourthDate={enableFourthLine}
        fourthDate={fourthDate}
        onFourthDateChange={setFourthDate}
      />

      {/* EPG Dropdown */}
      {epgLoading && (
        <div style={{
          marginBottom: 16,
          padding: '10px 16px',
          background: '#f3f4f6',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          color: '#6b7280',
          fontSize: 14
        }}>
          <span style={{
            width: 16,
            height: 16,
            border: '2px solid #d1d5db',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          Loading programs...
        </div>
      )}

      {!epgLoading && epgForSelectedDate.length > 0 && (
        <div className="epg-drawer" style={{
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <label style={{ fontWeight: 600, fontSize: 14 }}>
            Select Program:
          </label>

          <select
            value={selectedProgram?.id || ""}
            onChange={e => {
              const prog = epgForSelectedDate.find(p => p.id === e.target.value);
              setSelectedProgram(prog || null);

              if (prog) {
                const startH = String(Math.floor(prog.startMinute / 60)).padStart(2, "0");
                const startM = String(prog.startMinute % 60).padStart(2, "0");

                setStartTime(`${startH}:${startM}`);
                setEndTime(programEndClock(prog));
              } else {
                setStartTime("00:00");
                setEndTime("23:59");
              }
            }}
            style={{
              flex: 1,
              maxWidth: 600,
              padding: '8px 12px',
              fontSize: 14,
              border: '1px solid #d1d5db',
              borderRadius: 8
            }}
          >
            <option value="">00:00 - 23:59 | Full Day</option>

            {epgForSelectedDate.map(prog => {
              const startH = String(Math.floor(prog.startMinute / 60)).padStart(2, "0");
              const startM = String(prog.startMinute % 60).padStart(2, "0");
              const endH = String(Math.floor(prog.endMinute / 60)).padStart(2, "0");
              const endM = String(prog.endMinute % 60).padStart(2, "0");

              return (
                <option key={prog.id} value={prog.id}>
                  {startH}:{startM} - {endH}:{endM} | {prog.programName}
                </option>
              );
            })}
          </select>
        </div>
      )}

      <div className="chart-box">
        {hasData ? (
          enableFourthLine ? (
            <MultiLineAvgChart
              title="Average Active TV"
              series={series}
              refreshKey={refreshKey}
            />
          ) : isTV8 ? (
            <MultiLineAvgChartTV8
              title="Average Active TV"
              series={series}
              refreshKey={refreshKey}
              mode="tv8"
              startTime={startTime}
              endTime={endTime}
            />
          ) : (
            <MultiLineAvgChart
              title="Average Active TV"
              series={series}
              refreshKey={refreshKey}
            />
          )
        ) : (
          <div className="chart-empty">No data available</div>
        )}
      </div>
    </div>
  );
}